import React from "react";
import {
  Button,
  DownloadIcon,
  Modal,
  Pagination,
  PlusIcon,
} from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import ReportViolationForm from "~/components/Forms/ReportViolationForm";
import ViolationsFilter from "~/pages/Violations/Filter";
import Wrapper from "~/shared/ui/Wrapper";
import { useUserRole } from "~/hooks/useUserRole";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  getEndOfDayWithTimestamp,
  getFullTimeWithTimestamp,
} from "~/libs/getDateOptions";
import {
  getViolationsColumns,
  VIOLATION_SORTABLE_API_KEYS,
} from "~/pages/Violations/model/tableAdapter";
import { getViolationRowDecorator } from "~/pages/Violations/model/tableRowDecorator";
import { VIOLATION_TABLE_OPTION } from "~/pages/Violations/options";
import {
  FilterExistenceType,
  OrderByColumnInViolation,
  SortType,
  useGetAllReportViolationsLazyQuery,
  useGetViolationsAllQuery,
  ViolationStatus,
  ViolationTypeCategory,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useUser } from "~/shared/context/UserContext";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { useNotify } from "~/shared/hooks/useNotify";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import s from "./violations.less";

const reportFormId = "ReportViolationForm";

const Violations = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { dealerChoice } = useUser();
  const history = useHistory();
  const setData = useSetLocationData();
  const setDataReplace = useSetLocationData(true);
  const isDealer = dealerChoice === "one";

  const { notifyReport } = useNotify();

  const {
    openModal,
    page,
    count,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();
  // Ключи фильтров приходят из URL и не описаны в типе useGetLocationData
  const {
    documentFilter,
    statusFilter,
    FoFilter,
    regionFilter,
    fromFilter,
    toFilter,
    dealerIdFilter,
    violationTypesIdFilter,
    subdivisionIdFilter,
    categoryFilter,
    fromCreatedAt,
    toCreatedAt,
    dealerCandidate,
  } = useGetLocationData() as Record<string, unknown>;

  const currentDealerId = useCurrentDealerId();

  // Дилеру доступна сортировка только по сроку исполнения и наличию ответа
  const sortableApiKeys = isDealer
    ? [OrderByColumnInViolation.Deadline, OrderByColumnInViolation.Answer]
    : Object.values(VIOLATION_SORTABLE_API_KEYS);

  const { sortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: sortableApiKeys,
    setData,
  });

  const tableSortField =
    sortField && sortableApiKeys.includes(sortField as OrderByColumnInViolation)
      ? sortField
      : undefined;

  const { handleCount, handlePage } = usePaginationParams();

  const openAddModal = () => {
    history.push("/violations/violation_recording/new");
    setData("openType", "add");
  };

  const closeModal = () => setDataReplace("openModal", "");

  // Общие переменные фильтра для таблицы и отчёта по фильтру
  const filterVariables = {
    documentFilter: (documentFilter ? String(documentFilter) : undefined) as
      | FilterExistenceType
      | undefined,
    statusFilter: (statusFilter ? String(statusFilter) : undefined) as
      | ViolationStatus
      | undefined,
    FoFilter: FoFilter ? String(FoFilter) : undefined,
    regionFilter: regionFilter ? String(regionFilter) : undefined,
    categoryFilter: (categoryFilter ? String(categoryFilter) : undefined) as
      | ViolationTypeCategory
      | undefined,
    dealerIdFilter: isDealer
      ? undefined
      : dealerIdFilter
        ? String(dealerIdFilter)
        : undefined,
    violationTypesIdFilter: violationTypesIdFilter
      ? String(violationTypesIdFilter)
      : undefined,
    subdivisionIdFilter: subdivisionIdFilter
      ? String(subdivisionIdFilter)
      : undefined,
    dealerCandidate: dealerCandidate ? String(dealerCandidate) : undefined,
    fromFilter: fromFilter
      ? getFullTimeWithTimestamp(String(fromFilter))
      : undefined,
    toFilter: toFilter ? getEndOfDayWithTimestamp(String(toFilter)) : undefined,
    fromCreatedAt: fromCreatedAt
      ? getFullTimeWithTimestamp(String(fromCreatedAt))
      : undefined,
    toCreatedAt: toCreatedAt
      ? getEndOfDayWithTimestamp(String(toCreatedAt))
      : undefined,
  };

  const { data, loading } = useGetViolationsAllQuery({
    variables: {
      page,
      count: count || 25,
      order:
        tableSortField && sortType
          ? sortType === "DESC"
            ? SortType.Desc
            : SortType.Asc
          : undefined,
      orderBy: tableSortField as OrderByColumnInViolation | undefined,
      ...filterVariables,
    },
    fetchPolicy: "cache-and-network",
  });

  const [getAllReportViolationsLazyQuery] = useGetAllReportViolationsLazyQuery({
    onCompleted(data) {
      const hash = get(data, `ViolationGetter.getAllReport.fileHash`);
      hash && notifyReport(hash);
    },
    fetchPolicy: "no-cache",
  });

  const onSubmitFilterReport = () => {
    getAllReportViolationsLazyQuery({
      variables: {
        ...filterVariables,
      },
    });
  };

  const violations = get(data, "ViolationGetter.getAll.items")?.map((el) => ({
    id: el.id,
    punishment: el.punishment,
    dateFrom: el.createdAt,
    dateTo: el.fixDeadline,
    DocNum: el.DocNum,
    DocDate: el.DocDate,
    rationale: {
      fileExist: !!el.File,
      id: !!el.File && el.File.id,
      link: !!el.File && el.File.link,
    },
    answered: el.status === ViolationStatus.GotDealerResponse,
    answerStatus: el.status,
    reportedByUser: el.reportedByUser?.fullname,
    federalArea: {
      en: `${el.Dealer.FOEn}, ${el.Dealer.cityEn}`,
      ru: `${el.Dealer.FO}, ${el.Dealer.city}`,
    },
    dealerName: {
      en: {
        name: `${el.Dealer.DealerNameEn}`,
        other: `${el.Dealer.INN} ${el.Dealer.code}`,
      },
      ru: {
        name: `${el.Dealer.DealerName}`,
        other: `${el.Dealer.INN} ${el.Dealer.code}`,
      },
    },
    deadline: el.fixDeadline,
    violationType: el.ViolationType.name,
    subdivision: el.ViolationType.Subdivision.name,
    category: el.ViolationType.category,
    comments: el.ViolationComments || undefined,
  }));

  const total = get(data, "ViolationGetter.getAll.total");

  const myRole = useUserRole();
  const columns = getViolationsColumns(
    t,
    VIOLATION_TABLE_OPTION,
    myRole as string,
    tableSortField,
    sortType,
  );

  return (
    <Wrapper.Content
      title={t("violation.title")}
      filterComponent={<ViolationsFilter />}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("violations.create") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <Flex justify={"space-between"} className={s.actionsRow}>
        <Flex gap={10}>
          {hasPermission("violations.getReportByDealer") && (
            <Button
              color={"secondary"}
              variant={"filled"}
              bold
              fontSize="12"
              size={"middle"}
              radius={30}
              icon={<DownloadIcon size={16} fill="#495a64" />}
              onClick={() => setDataReplace("openModal", "Report")}
            >
              {t("violation.reportExcel")}
            </Button>
          )}
          {hasPermission("violations.getAllReport") && (
            <Button
              color={"secondary"}
              variant={"filled"}
              bold
              fontSize="12"
              size={"middle"}
              radius={30}
              icon={<DownloadIcon size={16} fill="#495a64" />}
              onClick={onSubmitFilterReport}
            >
              {t("violation.reportByFilter")}
            </Button>
          )}
        </Flex>
      </Flex>
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={violations}
        loading={loading}
        stripedRows={false}
        rowDecorator={getViolationRowDecorator}
        sortableColumns={sortableApiKeys}
        sortField={tableSortField}
        sortType={tableSortField ? sortType : undefined}
        onSortChange={onSortChange}
        onRow={(record) => ({
          onClick: () => {
            history.push(`/violations/description/${record.id}`);
            setData("page", page);
          },
        })}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <Modal
        open={openModal === "Report"}
        title={t("violation.reportExcelTitle")}
        onCancel={closeModal}
        closable
        centered
        destroyOnHidden
        okButton={{
          htmlType: "submit",
          form: reportFormId,
          children: t("ui.save"),
        }}
      >
        <ReportViolationForm setCloseModal={closeModal} formId={reportFormId} />
      </Modal>
    </Wrapper.Content>
  );
};

export default Violations;
